import type { BadgeProps } from '@/components/cargofl-ui/badge';
import type {
  AssetCondition,
  AssetOwnership,
  AssetRequestStatus,
  AssetRequestTiming,
  AssetStatus,
  ClearanceStatus,
  EmployeeStatus,
  RepairRequestStatus,
  RepairStatus,
  RepairUrgency,
  WarrantyState,
} from '@cfl/assets-shared';

type Variant = NonNullable<BadgeProps['variant']>;

/** Shared pill shape for status badges, matching CargoFL Performance. */
export const PILL_CLASS = 'rounded-full px-2.5 py-0.5 text-[11px] font-semibold';

/**
 * Asset status to a soft pill.
 *
 * The colour rules are the ones CargoFL Performance settled on, applied to this
 * domain:
 *
 * - **Assigned is blue (`info`), not amber.** An asset out with an employee is the
 *   system working, not a warning. Colouring the normal state as a problem is how a
 *   dashboard stops meaning anything - and in a healthy company most assets are
 *   assigned, so this is the majority of every screen.
 * - **Available is green**, because it is the state that lets you say yes to a new
 *   joiner.
 * - **Damaged and Lost are red**: both are money, and both need a decision.
 * - **Retired and Disposed are muted.** They are the settled end of a life, not a
 *   problem - amber on every disposed asset would put a warning across the archive.
 */
export function assetStatusVariant(status: AssetStatus): Variant {
  switch (status) {
    case 'AVAILABLE':
      return 'soft-success';
    case 'ASSIGNED':
      return 'info';
    case 'UNDER_REPAIR':
      return 'soft-warning';
    case 'DAMAGED':
    case 'LOST':
      return 'soft-destructive';
    case 'RETIRED':
    case 'DISPOSED':
    default:
      return 'soft-muted';
  }
}

/**
 * Who owns the asset.
 *
 * Company-owned is the overwhelming majority and carries no news, so it is muted -
 * and in the register it is not drawn at all, the same way an acknowledged handover
 * shows no tick. Employee-owned is blue rather than amber: somebody using their own
 * laptop is a normal arrangement, not a problem, but it changes what happens when
 * they leave, so it is worth seeing at a glance.
 */
export function ownershipVariant(ownership: AssetOwnership): Variant {
  return ownership === 'EMPLOYEE' ? 'info' : 'soft-muted';
}

export function employeeStatusVariant(status: EmployeeStatus): Variant {
  switch (status) {
    case 'ACTIVE':
      return 'soft-success';
    // Amber because it is a deadline: whatever this person holds has to come back by
    // a known date, and that is exactly when it is worth noticing.
    case 'NOTICE_PERIOD':
      return 'soft-warning';
    case 'INACTIVE':
      return 'info';
    case 'EXITED':
    default:
      return 'soft-muted';
  }
}

export function repairStatusVariant(status: RepairStatus): Variant {
  switch (status) {
    case 'COMPLETED':
      return 'soft-success';
    // In progress is routine work, not a warning - the same rule as Assigned above.
    case 'IN_PROGRESS':
      return 'info';
    case 'OPEN':
      return 'soft-warning';
    case 'CANCELLED':
    default:
      return 'soft-muted';
  }
}

/**
 * Clearance outcome.
 *
 * Only a full CLEARED is green, and the two partial outcomes are amber rather than
 * red: they are real, deliberate decisions a person made and recorded, not failures.
 * PENDING is the one that needs chasing.
 */
export function clearanceVariant(status: ClearanceStatus): Variant {
  switch (status) {
    case 'CLEARED':
      return 'soft-success';
    case 'CLEARED_WITH_COMMENTS':
    case 'PARTIALLY_CLEARED':
      return 'soft-warning';
    case 'PENDING':
    default:
      return 'soft-destructive';
  }
}

/**
 * Warranty standing.
 *
 * NONE is muted, not red. Most furniture has no warranty recorded, and flagging every
 * chair as a problem every day is precisely how people learn to ignore the colour.
 */
export function warrantyVariant(state: WarrantyState): Variant {
  switch (state) {
    case 'ACTIVE':
      return 'soft-success';
    case 'EXPIRING_SOON':
      return 'soft-warning';
    case 'EXPIRED':
      return 'soft-destructive';
    case 'NONE':
    default:
      return 'soft-muted';
  }
}

export const WARRANTY_LABELS: Record<WarrantyState, string> = {
  ACTIVE: 'In warranty',
  EXPIRING_SOON: 'Expiring soon',
  EXPIRED: 'Out of warranty',
  NONE: 'No warranty',
};

/** Condition at handover or return. DAMAGED is the only one that is a problem. */
export function conditionVariant(condition: AssetCondition): Variant {
  switch (condition) {
    case 'NEW':
    case 'GOOD':
      return 'soft-success';
    case 'FAIR':
      return 'info';
    case 'POOR':
      return 'soft-warning';
    case 'DAMAGED':
    default:
      return 'soft-destructive';
  }
}

/**
 * Whether a holder has confirmed receipt.
 *
 * Unacknowledged is amber, not red. It is a chase, not a fault - most of the time the
 * person simply has not opened the app yet.
 */
export function acknowledgementVariant(acknowledged: boolean): Variant {
  return acknowledged ? 'soft-success' : 'soft-warning';
}

/**
 * A repair request's standing.
 *
 * OPEN is amber, not red: reporting a problem is the system working, and colouring
 * every new report as critical would put red across the whole queue on a normal day.
 * Only DECLINED is destructive-coloured, because that is the outcome the reporter did
 * not want and needs to actually read.
 */
export function repairRequestVariant(status: RepairRequestStatus): Variant {
  switch (status) {
    case 'RESOLVED':
      return 'soft-success';
    // Routine progress, same rule as Assigned and In progress elsewhere.
    case 'ACKNOWLEDGED':
    case 'REPAIR_RAISED':
      return 'info';
    case 'OPEN':
      return 'soft-warning';
    case 'DECLINED':
    default:
      return 'soft-destructive';
  }
}

/** How badly it is affecting the person who reported it. Their view, not IT's. */
export function urgencyVariant(urgency: RepairUrgency): Variant {
  switch (urgency) {
    case 'BLOCKING':
      return 'soft-destructive';
    case 'NORMAL':
      return 'soft-warning';
    case 'LOW':
    default:
      return 'soft-muted';
  }
}

/**
 * An asset request's standing.
 *
 * FULFILLED is green because something actually went out - the only outcome where the
 * person who asked got what they needed. Withdrawn is muted rather than red: nobody did
 * anything wrong, the joiner simply did not start, and colouring it as a failure would
 * put red on a queue full of perfectly normal endings.
 */
export function assetRequestVariant(status: AssetRequestStatus): Variant {
  switch (status) {
    case 'FULFILLED':
      return 'soft-success';
    case 'ACKNOWLEDGED':
      return 'info';
    case 'OPEN':
      return 'soft-warning';
    case 'DECLINED':
      return 'soft-destructive';
    case 'CANCELLED':
    default:
      return 'soft-muted';
  }
}

/**
 * How a request sits against the date it was wanted by.
 *
 * NONE is muted, not red - the "not recorded is never red" rule again. Most requests
 * genuinely have no date, and flagging every one of them would put amber across a whole
 * screen of requests nobody is late on.
 */
export function assetRequestTimingVariant(timing: AssetRequestTiming): Variant {
  switch (timing) {
    case 'LATE':
      return 'soft-destructive';
    case 'DUE_SOON':
      return 'soft-warning';
    case 'FUTURE':
      return 'info';
    case 'NONE':
    default:
      return 'soft-muted';
  }
}

export const ASSET_REQUEST_TIMING_LABELS: Record<AssetRequestTiming, string> = {
  LATE: 'Date passed',
  DUE_SOON: 'Due soon',
  FUTURE: 'Not yet due',
  NONE: 'No date',
};
